import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Pipe, PipeTransform } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { Purchased } from '../schemas';
import { AgreementService, ProductService } from '../service';

interface SourceData {
  ClientCode: string;
  Number: string;
  Title: string;
  Type: 'Agreement' | 'Product';
  LinkBase: string;
  InvoiceAmount: number;
  Hours: Purchased[];
}

@Pipe({ name: 'totalCommissionAmount' })
export class TotalCommissionAmountPipe implements PipeTransform {
  transform(item: SourceData): number {
    const totalHours = item.Hours.filter(({ Status, Commissionable }) => Status === 20 && Commissionable)
      .map(({ Hours, HourlyRate }) => Hours * (HourlyRate || 0))
      .reduce((total, next) => total + next, 0);

    return totalHours;
  }
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './commission-page.html',
  imports: [
    DecimalPipe,
    DatePipe,
    TotalCommissionAmountPipe,
    RouterModule,
    MatTableModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class CommissionPage {
  columnsToShow = signal(['ClientCode', 'Number', 'Title', 'Type', 'InvoiceAmount', 'Commission', 'actions']);

  productService = inject(ProductService);
  agreementService = inject(AgreementService);

  products = computed(() => {
    const products = this.productService.products();
    return products
      .filter((op) => (op.PurchasedHours || []).some(({ Status, Commissionable }) => Status === 20 && Commissionable))
      .map((op) => {
        const hours = op.PurchasedHours.filter(({ Status, Commissionable }) => Status === 20 && Commissionable);

        return {
          ClientCode: op.ClientCode,
          Hours: hours,
          Number: op.JobNumber,
          Title: op.Title,
          Type: 'Product',
          LinkBase: '/app/products/view',
          InvoiceAmount: 0,
        } as SourceData;
      });
  });

  agreements = computed(() => {
    const agreements = this.agreementService.agreements();
    return agreements
      .filter((ag) => ag.PurchasedHours.some(({ Status, Commissionable }) => Status === 20 && Commissionable))
      .map((ag) => {
        const hours = ag.PurchasedHours.filter(({ Status, Commissionable }) => Status === 20 && Commissionable);

        return {
          ClientCode: ag.ClientCode,
          Hours: hours,
          Number: ag.AgreementNumber,
          Title: ag.Title,
          Type: 'Agreement',
          LinkBase: '/app/agreements/view',
          InvoiceAmount: 0,
        } as SourceData;
      });
  });

  sourceData = computed(() => {
    const products = this.products();
    const agreements = this.agreements();
    const data: SourceData[] = [...products, ...agreements];

    data.forEach((it) => {
      it.Hours = it.Hours.map((item) => {
        return {
          ...item,
          InvoiceAmount: item.Hours * (item.HourlyRate || 0),
        };
      });
    });
    return data;
  });

  invoices = computed(() => {
    const entries = this.sourceData().map(({ Number, Hours }) => [
      Number,
      // Sort hours by invoice date descending (most recent first)
      Hours.sort(({ InvoiceDate: a }, { InvoiceDate: b }) => +new Date(b) - +new Date(a)),
    ]);

    return Object.fromEntries(entries);
  });

  source = computed(() => {
    const source = new MatTableDataSource(this.sourceData());

    return source;
  });

  trackBy(idx: number, row: SourceData) {
    return row.Number;
  }

  trackByHours(idx: number, row: Purchased) {
    return row.Id;
  }

  expanded = signal<Record<string, boolean>>({});

  toggle(key: string) {
    this.expanded.update((data) => ({
      ...data,
      [key]: !data[key],
    }));
  }
}
